/**
 * Where this meeting has got to.
 *
 * The work spans days — you schedule on Monday, hold it on Tuesday, upload the
 * recording that evening, name the speakers on Wednesday, approve on Friday — so
 * the single most useful thing the page can tell a returning user is which of
 * those has already happened and what is next. A status chip alone says where
 * you are but not how far that is through, nor what remains.
 *
 * A failed transcription is drawn on the step it failed at rather than as a
 * sixth state, because that is where it has to be resumed from.
 */

import { TbCheck, TbAlertTriangle, TbLoader2 } from 'react-icons/tb';

const STEPS = [
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'recording', label: 'Recording' },
  { key: 'transcript', label: 'Transcript' },
  { key: 'draft', label: 'Draft' },
  { key: 'approved', label: 'Approved' },
];

/** Which step the meeting's status sits on. */
const INDEX = {
  scheduled: 0,
  processing: 1,
  failed: 1,
  transcript_ready: 2,
  draft_ready: 3,
  approved: 4,
};

export default function PipelineStrip({ status }) {
  const current = INDEX[status] ?? 0;
  const failed = status === 'failed';
  const working = status === 'processing';

  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-2" aria-label="Meeting progress">
      {STEPS.map((step, i) => {
        const done = i < current;
        const here = i === current;
        const problem = here && failed;

        const dot = problem
          ? 'border-[#c4503f] bg-[#fdf3f1] text-[#a83c2c]'
          : done
            ? 'border-[#145f5f] bg-[#145f5f] text-white'
            : here
              ? 'border-[#145f5f] bg-white text-[#145f5f]'
              : 'border-[#ddd6c6] bg-white text-[#b0a695]';

        return (
          <li key={step.key} className="flex items-center gap-1">
            <div className="flex items-center gap-1.5">
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] text-[10px] font-bold ${dot}`}
                aria-hidden="true"
              >
                {problem ? <TbAlertTriangle size={10} />
                  : done ? <TbCheck size={11} />
                    : working && here ? <TbLoader2 size={10} className="animate-spin" />
                      : i + 1}
              </span>
              <span className={`text-[11px] ${
                problem ? 'font-bold text-[#a83c2c]'
                  : here ? 'font-bold text-[#2c2a24]'
                    : done ? 'text-[#5c5445]' : 'text-[#a09786]'
              }`}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <span
                aria-hidden="true"
                className={`mx-1 hidden h-px w-6 sm:inline-block ${done ? 'bg-[#145f5f]' : 'bg-[#ddd6c6]'}`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
